import React from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGetScalingSize } from '../report-style.helpers';
import { mergeSx } from '@client/helpers/mui.helpers';
import type { CSSProperties } from 'react';

/**
 * Font sizes are chosen from vw units on a 1440px screen adjusted to make Reports look good.
 * They will use vw or cqw units depending on Report.scalingStrategy
 */
const FONT_SIZES: Record<ReportTextElement, number> = {
  h1: 2.8,
  h2: 2.1,
  h3: 1.75,
  h4: 1.4,
  h5: 1.26,
  h6: 1.05,
  p: 0.84,
  span: 0.84,
};

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
  fontSizeOverride?: number;
}>()((theme, { getScalingSize, fontSizeOverride }) => {
  return {
    h1: { fontSize: getScalingSize(fontSizeOverride ?? FONT_SIZES.h1) },
    h2: { fontSize: getScalingSize(fontSizeOverride ?? FONT_SIZES.h2) },
    h3: { fontSize: getScalingSize(fontSizeOverride ?? FONT_SIZES.h3) },
    h4: { fontSize: getScalingSize(fontSizeOverride ?? FONT_SIZES.h4) },
    h5: { fontSize: getScalingSize(fontSizeOverride ?? FONT_SIZES.h5) },
    h6: { fontSize: getScalingSize(fontSizeOverride ?? FONT_SIZES.h6) },
    p: { fontSize: getScalingSize(fontSizeOverride ?? FONT_SIZES.p) },
    span: { fontSize: getScalingSize(fontSizeOverride ?? FONT_SIZES.span) },
  };
});

type ReportTextElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';

type ReportTextProps = {
  as: ReportTextElement;
  inline?: boolean;
  children: React.ReactNode;
  color?: TypographyProps['color'];
  fontSize?: number;
  fontWeight?: CSSProperties['fontWeight'];
  fontStyle?: CSSProperties['fontStyle'];
  textAlign?: CSSProperties['textAlign'];
  whiteSpace?: CSSProperties['whiteSpace'];
  textTransform?: CSSProperties['textTransform'];
  sx?: TypographyProps['sx'];
};

export const ReportText: React.FC<ReportTextProps> = ({
  as: element,
  children,
  color,
  fontSize,
  fontWeight,
  fontStyle,
  textAlign,
  whiteSpace,
  textTransform,
  sx,
}) => {
  const getScalingSize = useGetScalingSize();
  const { classes } = useStyles({
    getScalingSize,
    fontSizeOverride: fontSize,
  });

  return (
    <Typography
      component={element}
      className={classes[element]}
      color={color}
      sx={mergeSx(
        {
          fontWeight,
          fontStyle,
          textAlign,
          textTransform,
          whiteSpace,
        },
        sx,
      )}
    >
      {children}
    </Typography>
  );
};
